import { useContext, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  BreadBudget,
  BreadTransaction,
  FirestoreDocCategory,
  FirestoreDocCategoryGroup,
} from "breadcommon";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";
import {
  categoryGroupColor,
  COLORED_TYPE_BY_CATEGORY_TYPE,
  ColoredCategoryType,
} from "../common/categoryTypeColor";
import type { GroupedBarGroup, GroupedBarRow } from "../common/groupedBarRows";
import { getSignedBudgetLineItemAmount } from "./budgetUtils";
import {
  getFractionOfTimeIntoBudget,
  getTransactionsToDate,
  sumTransactions,
} from "./budgetTrackingUtils";
import { useGetGroupedCategoriesForBudget } from "./useGetGroupedCategoriesForBudget";

export const UNREVIEWED_ROW_ID = "__unreviewed__";

export type BudgetBarColor = ColoredCategoryType | "unreviewed";

/**
 * One row of the budget tracking bars: a group, a category inside one, or the
 * unreviewed transactions. Amounts are signed the way budget tracking signs
 * them: expenses negative, income and savings positive.
 */
export interface BudgetBarRow extends GroupedBarRow {
  /** The whole period's budget; zero when nothing is budgeted. */
  budget: number;
  /** What the budget comes to by now, spread evenly over its period. */
  budgetedToDate: number;
  actual: number;
  /** Whether any of the row's categories has a budget line. */
  hasBudget: boolean;
  /** Which color to draw the bar in; null when there's nothing to draw. */
  color: BudgetBarColor | null;
  transactions: BreadTransaction[];
  currency: string;
}

export type BudgetBarGroup = GroupedBarGroup<BudgetBarRow>;

/**
 * Every category, grouped and in category-list order, with its budget, its
 * budget to date, and what actually happened over the budget's period.
 * `groupedCategories` supplies that order and already leaves out Silent
 * categories and empty groups. Transactions are expected to have already been
 * converted into the budget's currency.
 *
 * A group's figures are the signed sums of its categories', and it's colored
 * by the mixed-type rule budget tracking has always used. Narrowed to one
 * `type`, only that type's categories are shown and groups without any are
 * left out.
 *
 * Unreviewed transactions only have a tentative category, so they count
 * toward no category; their total gets a row of its own at the top. That row
 * appears only for "all", since unreviewed transactions can't be sorted by
 * type.
 */
export function buildBudgetBars({
  transactions,
  budget,
  groupedCategories,
  categoryGroups,
  type,
  now,
}: {
  transactions: BreadTransaction[];
  budget: BreadBudget;
  groupedCategories: Map<string, Map<string, FirestoreDocCategory>>;
  categoryGroups: Map<string, FirestoreDocCategoryGroup>;
  type: ColoredCategoryType | "all";
  now: Dayjs;
}): BudgetBarGroup[] {
  const inPeriod = getTransactionsToDate(transactions, budget);
  const fractionIntoBudget = getFractionOfTimeIntoBudget(budget, now);
  const currency = budget.currency;

  const reviewedByCategory = new Map<string, BreadTransaction[]>();
  const unreviewed: BreadTransaction[] = [];
  inPeriod.forEach((transaction) => {
    if (!transaction.reviewed) {
      unreviewed.push(transaction);
    } else if (transaction.categoryId) {
      const list = reviewedByCategory.get(transaction.categoryId) ?? [];
      list.push(transaction);
      reviewedByCategory.set(transaction.categoryId, list);
    }
  });

  const groups = Array.from(groupedCategories.entries()).flatMap(
    ([groupId, allCategories]): BudgetBarGroup[] => {
      const categories = Array.from(allCategories.values()).filter(
        (category) =>
          type === "all" ||
          COLORED_TYPE_BY_CATEGORY_TYPE[category.type] === type,
      );
      if (categories.length === 0) return [];

      const categoryRows: BudgetBarRow[] = categories.map((category) => {
        const lineItem = budget.line_items.get(category.id);
        const categoryBudget = lineItem
          ? getSignedBudgetLineItemAmount(lineItem, category)
          : 0;
        const categoryTransactions = reviewedByCategory.get(category.id) ?? [];
        return {
          id: category.id,
          kind: "category",
          groupId,
          label: category.emoji
            ? `${category.emoji} ${category.name}`
            : category.name,
          budget: categoryBudget,
          budgetedToDate: categoryBudget * fractionIntoBudget,
          actual: sumTransactions(categoryTransactions),
          hasBudget: lineItem !== undefined,
          color: COLORED_TYPE_BY_CATEGORY_TYPE[category.type] ?? null,
          transactions: categoryTransactions,
          currency,
        };
      });

      const sum = (pick: (row: BudgetBarRow) => number) =>
        categoryRows.reduce((total, row) => total + pick(row), 0);
      const actual = sum((row) => row.actual);
      const groupTransactions = categoryRows.flatMap((row) => row.transactions);
      return [
        {
          id: groupId,
          kind: "group",
          groupId,
          label: categoryGroups.get(groupId)?.name ?? "Ungrouped",
          budget: sum((row) => row.budget),
          budgetedToDate: sum((row) => row.budgetedToDate),
          actual,
          hasBudget: categoryRows.some((row) => row.hasBudget),
          color: categoryGroupColor(categories, actual, groupTransactions),
          transactions: groupTransactions,
          currency,
          categories: categoryRows,
        },
      ];
    },
  );

  if (type !== "all" || unreviewed.length === 0) return groups;
  return [
    {
      id: UNREVIEWED_ROW_ID,
      kind: "group",
      groupId: UNREVIEWED_ROW_ID,
      label: "Unreviewed",
      budget: 0,
      budgetedToDate: 0,
      actual: sumTransactions(unreviewed),
      hasBudget: false,
      color: "unreviewed",
      transactions: unreviewed,
      currency,
      categories: [],
    },
    ...groups,
  ];
}

/** How the bars are scaled: in dollars, or each row to its own budget. */
export type BudgetBarScale = "dollars" | "budget";

/**
 * The lengths a row's bar is drawn from, in the chart's units: the budget
 * band, the actual amount, and the pace, plus `value`, the longest of
 * them, which sets the bar's overall length.
 *
 * Scaled to its budget, a row's budget is always 1, so every row's band ends
 * at the same place and its actual and pace read as fractions of it. A row
 * with no budget has nothing to scale to, so it draws nothing there.
 */
export function budgetBarLengths(
  row: BudgetBarRow,
  scale: BudgetBarScale,
): { value: number; budget: number; actual: number; pace: number } {
  if (scale === "budget") {
    if (row.budget === 0) return { value: 0, budget: 0, actual: 0, pace: 0 };
    const actual = Math.abs(row.actual / row.budget);
    return {
      value: Math.max(1, actual),
      budget: 1,
      actual,
      pace: Math.abs(row.budgetedToDate / row.budget),
    };
  }
  const budget = Math.abs(row.budget);
  const actual = Math.abs(row.actual);
  return {
    value: Math.max(budget, actual),
    budget,
    actual,
    pace: Math.abs(row.budgetedToDate),
  };
}

/**
 * How much of the budget is left, in the budget's own direction: positive
 * while there's room, negative once it's been overrun. For an income budget,
 * that's how much is still to come in. Null when nothing is budgeted.
 */
export function budgetLeft(row: BudgetBarRow): number | null {
  if (!row.hasBudget || row.budget === 0) return null;
  return (row.budget - row.actual) * Math.sign(row.budget);
}

/**
 * How far the actual amount is past what the budget comes to by now, in the
 * budget's direction: positive when more has happened than expected so far.
 * Null when nothing is budgeted.
 */
export function aheadOfPace(row: BudgetBarRow): number | null {
  if (!row.hasBudget || row.budget === 0) return null;
  return (row.actual - row.budgetedToDate) * Math.sign(row.budget);
}

export function useBudgetBars(
  budget: BreadBudget,
  type: ColoredCategoryType | "all",
): BudgetBarGroup[] {
  const transactions = useContext(TransactionsContext);
  const categories = useContext(CategoriesContext);
  const categoryGroups = useContext(CategoryGroupsContext);
  const groupedCategories = useGetGroupedCategoriesForBudget();
  const { convertTransactionsToSpecificCurrency } = useExchangeRates();

  return useMemo(
    () =>
      buildBudgetBars({
        transactions: convertTransactionsToSpecificCurrency(
          Array.from(transactions.values()),
          budget.currency,
        ),
        budget,
        groupedCategories,
        categoryGroups,
        type,
        now: dayjs(),
      }),
    // groupedCategories is rebuilt on every render, so this is keyed by the
    // categories and groups it's built from instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      transactions,
      budget,
      categories,
      categoryGroups,
      type,
      convertTransactionsToSpecificCurrency,
    ],
  );
}
